import { useEffect } from "react";
import { usePermissions } from "../hooks/usePermissions";
import { useOrganization } from "../context/OrganizationContext";
import {
  canAccessScreen,
  getDeniedRedirectScreen,
} from "../helpers/screenGuards";
import {
  GOOGLE_VISIBILITY_ROUTE_DECISION,
  GOOGLE_VISIBILITY_SCREEN,
  resolveGoogleVisibilityRouteDecision,
} from "../helpers/googleVisibilityAccess";
import { isProjectAccessibleToActor } from "../helpers/teamScope";
import { resolveOrgPolicies } from "../helpers/permissions";

/**
 * Route guard that sees OrganizationContext settings (Finance module flag).
 * Must render inside AppProviders.
 */
export function AppScreenAccessGuard({
  screen,
  currentUser,
  directReportIds,
  selectedProject,
  onDenied,
}) {
  const { orgPolicies: permissionPolicies } = usePermissions();
  const { settings } = useOrganization();

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    if (screen === GOOGLE_VISIBILITY_SCREEN) {
      const decision = resolveGoogleVisibilityRouteDecision(currentUser, true);
      if (decision === GOOGLE_VISIBILITY_ROUTE_DECISION.WAIT) {
        return;
      }
    }

    const projectAssigned =
      screen === "project_detail" && selectedProject
        ? isProjectAccessibleToActor(
            currentUser,
            selectedProject,
            directReportIds,
          )
        : false;

    const context =
      screen === "project_detail" ? { projectAssigned } : {};

    const orgPolicies =
      permissionPolicies || resolveOrgPolicies(settings || {});

    if (
      !canAccessScreen(
        currentUser,
        screen,
        context,
        orgPolicies,
        directReportIds,
      )
    ) {
      onDenied?.(getDeniedRedirectScreen(currentUser, orgPolicies));
    }
  }, [
    currentUser,
    directReportIds,
    onDenied,
    permissionPolicies,
    screen,
    selectedProject,
    settings,
  ]);

  return null;
}
